import { useState, useMemo, useCallback } from 'react';
import { isProjectNetSRPActive } from '../common/helpers/constants';
import { transformSuggestionProjectData } from '../common/helpers/data/transformProjectnetProjectData';
import { fetchSearchSuggestions } from '../services/search';
import { fetchProjectnetSuggestions } from '../frontend-service/project-api';
import logger from '../common/helpers/logger/logger';
import { get } from 'lodash';

export const useSearchSuggestions = ({ client }) => {
  const [isSearchInProgress, setSearchInProgress] = useState(false);
  const [shouldShowResult, setShouldShowResult] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [oldSearchValue, setOldSearchValue] = useState('');

  const hasResult = useMemo(() => Boolean(suggestions.length), [suggestions]);

  const onSearch = useCallback(
    async (searchValue, isRouteChanged) => {
      const isProjectNet = isProjectNetSRPActive();

      try {
        const response = await (isProjectNet
          ? fetchProjectnetSuggestions(searchValue)
          : fetchSearchSuggestions(client, { keyword: searchValue }));

        const newSuggestions = isProjectNet
          ? transformSuggestionProjectData(response?.data || [])
          : get(response, 'data.suggestion.items', []);

        if (!isRouteChanged && newSuggestions.length > 0) {
          setShouldShowResult(true);
          setSuggestions(newSuggestions);
        } else {
          setShouldShowResult(false);
          setSuggestions([]);
        }
      } catch (error) {
        setShouldShowResult(false);
        setSuggestions([]);
        logger.logError(
          'onSearch',
          'Error retrieving project suggestions data',
          error,
        );
      } finally {
        setSearchInProgress(false);
        setOldSearchValue(searchValue);
      }
    },
    [client],
  );

  const initiateSearch = useCallback(
    (searchValue, isRouteChanged) => {
      if (oldSearchValue === searchValue && hasResult) {
        setShouldShowResult(true);
      } else {
        setSearchInProgress(true);
        onSearch(searchValue, isRouteChanged);
      }
    },
    [oldSearchValue, hasResult, onSearch],
  );

  return {
    initiateSearch,
    setShouldShowResult,
    setSearchInProgress,
    isSearchInProgress,
    shouldShowResult,
    suggestions,
  };
};
